import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Smartphone, MessageSquare, Search, ShieldAlert, MapPinOff, FileWarning, Clock, Hourglass } from 'lucide-react';
export function ProblemSection() {
  const problems = [{
    icon: <Smartphone className="w-6 h-6 text-gray-700" data-id="element-226" />,
    text: 'Fotos en 5 celulares distintos'
  }, {
    icon: <MessageSquare className="w-6 h-6 text-gray-700" data-id="element-227" />,
    text: 'Grupos de WhatsApp desordenados'
  }, {
    icon: <Search className="w-6 h-6 text-gray-700" data-id="element-228" />,
    text: 'Nadie encuentra nada cuando se necesita'
  }, {
    icon: <ShieldAlert className="w-6 h-6 text-gray-700" data-id="element-229" />,
    text: 'Cuando hay conflicto, no hay respaldo'
  }, {
    icon: <MapPinOff className="w-6 h-6 text-gray-700" data-id="element-230" />,
    text: 'Las fotos no están asociadas a un lugar específico del plano'
  }, {
    icon: <FileWarning className="w-6 h-6 text-gray-700" data-id="element-231" />,
    text: 'No existe trazabilidad formal de la información'
  }, {
    icon: <Clock className="w-6 h-6 text-gray-700" data-id="element-232" />,
    text: 'Se pierde evidencia con el tiempo'
  }, {
    icon: <Hourglass className="w-6 h-6 text-gray-700" data-id="element-233" />,
    text: 'Buscar una foto antigua puede tomar horas'
  }];
  return <section id="problema" className="py-16 sm:py-24 bg-gray-50 overflow-hidden" data-id="element-234">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-235">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center" data-id="element-236">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="mb-12 lg:mb-0" data-id="element-237">
            <h2 className="text-sm font-semibold text-blue-600 tracking-wide uppercase mb-2" data-id="element-238">
              El problema
            </h2>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-239">
              El caos de la información en obra
            </h3>

            <ul className="space-y-4 sm:space-y-6 mb-8 sm:mb-10" data-id="element-240">
              {problems.map((problem, index) => <motion.li key={index} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="flex items-start" data-id="element-241">
                  <div className="flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm" data-id="element-242">
                    {problem.icon}
                  </div>
                  <p className="ml-3 sm:ml-4 text-base sm:text-lg text-gray-700 mt-1 sm:mt-1.5" data-id="element-243">
                    {problem.text}
                  </p>
                </motion.li>)}
            </ul>

            <div className="inline-block bg-blue-50 border border-blue-100 rounded-lg px-4 sm:px-6 py-3 sm:py-4" data-id="element-244">
              <p className="text-lg sm:text-xl font-bold text-blue-900" data-id="element-245">
                Eso es <span className="text-blue-600" data-id="element-246">costo oculto.</span>
              </p>
            </div>

            <div className="mt-3 sm:mt-4 bg-red-50 border border-red-100 rounded-lg px-4 sm:px-6 py-3 sm:py-4" data-id="element-247">
              <p className="text-lg sm:text-xl font-bold text-red-800" data-id="element-248">
                WhatsApp no es un sistema de registro técnico.
              </p>
            </div>
          </motion.div>

          {/* Chaos Illustration */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative h-[300px] sm:h-[400px] w-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex items-center justify-center" data-id="element-249">
            {/* Background grid pattern */}
            <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(#e5e7eb 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }} data-id="element-250"></div>

            <div className="relative w-full h-full" data-id="element-251">
              {/* Scattered Photos */}
              <motion.div animate={{
              rotate: [-12, -10, -12]
            }} transition={{
              repeat: Infinity,
              duration: 4
            }} className="absolute top-8 sm:top-12 left-6 sm:left-10 w-24 sm:w-32 h-24 sm:h-32 border-4 border-white shadow-md rounded-sm -rotate-12 z-10 overflow-hidden bg-gray-100" data-id="element-252">
                <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300&h=300&fit=crop" alt="Foto desordenada 1" className="w-full h-full object-cover" loading="lazy" data-id="element-253" />
                <div className="absolute bottom-2 right-2 w-6 h-6 bg-red-400 rounded-full flex items-center justify-center text-white text-xs" data-id="element-254">
                  !
                </div>
              </motion.div>

              <motion.div animate={{
              rotate: [8, 10, 8]
            }} transition={{
              repeat: Infinity,
              duration: 5
            }} className="absolute top-24 sm:top-32 right-6 sm:right-12 w-20 sm:w-28 h-20 sm:h-28 border-4 border-white shadow-md rounded-sm rotate-6 z-20 overflow-hidden bg-gray-100" data-id="element-255">
                <img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=300&h=300&fit=crop" alt="Foto desordenada 2" className="w-full h-full object-cover" loading="lazy" data-id="element-256" />
              </motion.div>

              <motion.div animate={{
              rotate: [-5, -3, -5]
            }} transition={{
              repeat: Infinity,
              duration: 4.5
            }} className="absolute bottom-10 sm:bottom-16 left-16 sm:left-24 w-28 sm:w-36 h-20 sm:h-24 border-4 border-white shadow-md rounded-sm -rotate-3 z-10 overflow-hidden bg-gray-100" data-id="element-257">
                <img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=300&h=300&fit=crop" alt="Foto desordenada 3" className="w-full h-full object-cover" loading="lazy" data-id="element-258" />
              </motion.div>

              {/* Chat Bubbles */}
              <div className="absolute top-16 sm:top-24 left-1/2 -translate-x-1/2 bg-green-100 border border-green-200 p-2 sm:p-3 rounded-2xl rounded-tr-none shadow-sm z-30 max-w-[140px] sm:max-w-[180px]" data-id="element-259">
                <div className="h-2 w-16 sm:w-24 bg-green-300 rounded mb-2" data-id="element-260"></div>
                <div className="h-2 w-10 sm:w-16 bg-green-300 rounded" data-id="element-261"></div>
              </div>

              <div className="absolute bottom-24 sm:bottom-32 right-12 sm:right-24 bg-white border border-gray-200 p-2 sm:p-3 rounded-2xl rounded-tl-none shadow-sm z-30 max-w-[120px] sm:max-w-[150px]" data-id="element-262">
                <div className="h-2 w-14 sm:w-20 bg-gray-300 rounded mb-2" data-id="element-263"></div>
                <div className="h-2 w-8 sm:w-12 bg-gray-300 rounded" data-id="element-264"></div>
              </div>

              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-50 border border-red-100 p-2 sm:p-3 rounded-2xl rounded-bl-none shadow-sm z-40 max-w-[170px] sm:max-w-[200px] rotate-3" data-id="element-265">
                <p className="text-[10px] sm:text-xs text-red-600 font-medium" data-id="element-266">
                  "¿Dónde está la foto de la losa del martes?"
                </p>
              </div>

              {/* Red notification dots scattered */}
              <div className="absolute top-20 right-32 w-3 h-3 bg-red-500 rounded-full" data-id="element-267"></div>
              <div className="absolute bottom-40 left-16 w-3 h-3 bg-red-500 rounded-full" data-id="element-268"></div>
              <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-red-500 rounded-full" data-id="element-269"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}